<script setup>
import AdminEmpresaSuscripcionEstado from '@/components/AdminEmpresaSuscripcionEstado.vue'
</script>
<template>
  <div :class="['empresas-container', themeClass]">
    <!-- Header Simplificado -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          <i class="fas fa-building me-2"></i>
          Listado de Empresas
        </h1>
        <p class="page-subtitle">
          <i class="fas fa-cogs me-1"></i>
          Gestiona las empresas registradas que usan el sistema de facturación electrónica. 
        </p>
      </div>
      <router-link :to="{ name: 'EmpresasCrear' }" class="btn-primary-custom">
        <i class="fas fa-plus"></i>
        Nueva Empresa
      </router-link>
    </div>

    <!-- Estadísticas Minimalistas -->
    <div class="stats-grid" v-if="computedStats">
      <div class="stat-card stat-total">
      <div class="stat-number">{{ computedStats.total_empresas || 0 }}</div>
      <div class="stat-label">Total</div>
      </div>
      <div class="stat-card stat-active">
      <div class="stat-number">{{ computedStats.empresas_activas || 0 }}</div>
      <div class="stat-label">Activas</div>
      </div>
      <div class="stat-card stat-inactive">
      <div class="stat-number">{{ computedStats.empresas_inactivas || 0 }}</div>
      <div class="stat-label">Inactivas</div>
      </div>
      <div class="stat-card stat-suspended">
      <div class="stat-number">{{ computedStats.empresas_suspendidas || 0 }}</div>
      <div class="stat-label">Suspendidas</div>
      </div>
    </div>

    <!-- Filtros Simplificados -->
    <div class="filters-section">
      <div class="search-box">
        <i class="fas fa-search search-icon"></i>
        <input
          v-model="filters.search"
          @input="debouncedSearch"
          type="text"
          placeholder="Buscar empresas..."
          class="search-input"
        />
      </div>
      <div class="filter-group">
        <select v-model="filters.status" @change="loadEmpresas" class="filter-select">
          <option value="">Todos los estados</option>
          <option value="active">Activas</option>
          <option value="inactive">Inactivas</option>
          <option value="suspended">Suspendidas</option>
        </select>
        <select v-model="filters.company_type" @change="loadEmpresas" class="filter-select">
          <option value="">Todos los tipos</option>
          <option value="natural">Natural</option>
          <option value="juridica">Jurídica</option>
        </select>
      </div>
    </div>

    <!-- Lista de Empresas -->
    <div class="empresas-list">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>
          <i class="fas fa-clock me-1"></i>
          Cargando empresas...
        </p>
      </div>

      <div v-else-if="empresas.length === 0" class="empty-state">
        <i class="fas fa-building-slash"></i>
        <h3>
          <i class="fas fa-exclamation-triangle me-2"></i>
          No hay empresas
        </h3>
        <p>
          <i class="fas fa-filter me-1"></i>
          No se encontraron empresas con los filtros aplicados
        </p>
      </div>

      <!-- Tabla de empresas -->
      <div v-else class="table-responsive" style="overflow: visible;">
        <table class="table table-hover align-middle">
          <thead class="table-light">
            <tr>
              <th scope="col" class="fw-bold">RUC</th>
              <th scope="col" class="fw-bold">Razón Social</th>
              <th scope="col" class="fw-bold">Estado</th>
              <th scope="col" class="fw-bold">Correo</th>
              <th scope="col" class="fw-bold">WhatsApp</th>
              <th scope="col" class="fw-bold">Tipo Empresa</th>
              <th scope="col" class="fw-bold text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="empresa in empresas" :key="empresa.id">
              <td>
                <span class="font-monospace text-primary fw-bold">{{ empresa.ruc }}</span>
                <AdminEmpresaSuscripcionEstado :ruc="empresa.ruc" />
              </td>
              <td>
                <div>
                  <strong class="text-dark">{{ empresa.razon_social }}</strong>
                  <br>
                  <small class="text-muted" v-if="empresa.trade_name && empresa.trade_name !== empresa.razon_social">
                    {{ empresa.trade_name }}
                  </small>
                </div>
              </td>
              <td>
                <span :class="`status-badge status-${empresa.status}`">
                  {{ getStatusText(empresa.status) }}
                </span>
              </td>
              <td>
                <div class="d-flex align-items-center">
                  <i class="fas fa-envelope text-muted me-2"></i>
                  <span>{{ empresa.email }}</span>
                </div>
              </td>
              <td>
                <div class="d-flex align-items-center" v-if="empresa.whatsapp || empresa.legal_representative_phone">
                  <i class="fab fa-whatsapp text-success me-2"></i>
                  <span>{{ empresa.whatsapp || empresa.legal_representative_phone || 'No especificado' }}</span>
                </div>
                <span v-else class="text-muted">No especificado</span>
              </td>
              <td>
                <span class="badge" :class="{
                  'bg-primary': empresa.company_type === 'juridica',
                  'bg-success': empresa.company_type === 'natural',
                  'bg-info': empresa.company_type === 'publico',
                  'bg-secondary': !empresa.company_type
                }">
                  {{ getCompanyTypeText(empresa.company_type) }}
                </span>
              </td>
              <td class="text-center">
                <div class="action-buttons" role="group">
                  <button @click="viewEmpresa(empresa)" class="btn btn-outline-primary" title="Ver detalles">
                    <i class="fas fa-eye"></i>
                  </button>
                  <router-link 
                    :to="{ name: 'EmpresasEditar', params: { id: empresa.id } }" 
                    class="btn btn-outline-secondary" 
                    title="Editar empresa"
                  >
                    <i class="fas fa-edit"></i>
                  </router-link>
                  <div class="dropdown action-dropdown">
                    <button 
                      :id="`dropdownButton${empresa.id}`"
                      class="btn btn-outline-secondary dropdown-toggle" 
                      type="button"
                      @click="toggleDropdown($event, empresa)"
                      title="Más opciones"
                      aria-expanded="false"
                    >
                      <i class="fas fa-ellipsis-v"></i>
                    </button>

                    <teleport to="body">
                      <div
                        v-if="activeDropdown === empresa.id"
                        class="dropdown-menu dropdown-menu-end show"
                        :style="dropdownStyle"
                        @click.stop
                      >
                        <ul class="list-unstyled mb-0">
                          <li class="dropdown-header">Estado de la empresa</li>
                          <li v-if="empresa.status !== 'active'">
                            <a class="dropdown-item" href="#" @click.prevent="changeStatus(empresa.id, 'active')">
                              <i class="fas fa-check-circle text-success me-2"></i>Activar empresa
                            </a>
                          </li>
                          <li v-if="empresa.status !== 'inactive'">
                            <a class="dropdown-item" href="#" @click.prevent="changeStatus(empresa.id, 'inactive')">
                              <i class="fas fa-pause-circle text-warning me-2"></i>Desactivar empresa
                            </a>
                          </li>
                          <li v-if="empresa.status !== 'suspended'">
                            <a class="dropdown-item" href="#" @click.prevent="changeStatus(empresa.id, 'suspended')">
                              <i class="fas fa-ban text-danger me-2"></i>Suspender empresa
                            </a>
                          </li>
                          <li>
                            <a class="dropdown-item text-danger" href="#" @click.prevent="deleteEmpresa(empresa)">
                              <i class="fas fa-trash me-2"></i>Eliminar empresa
                            </a>
                          </li>
                        </ul>
                      </div>
                    </teleport>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Paginación Simple -->
    <nav v-if="pagination.total_pages > 1" class="mt-4">
      <ul class="pagination justify-content-center">
        <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
          <button
            class="page-link"
            @click="changePage(pagination.current_page - 1)"
            :disabled="pagination.current_page === 1"
          >
            <i class="fas fa-chevron-left me-1"></i>
            Anterior
          </button>
        </li>
        
        <li
          v-for="page in getVisiblePages()"
          :key="page"
          class="page-item"
          :class="{ active: page === pagination.current_page }"
        >
          <button class="page-link" @click="changePage(page)">
            {{ page }}
          </button>
        </li>
        
        <li class="page-item" :class="{ disabled: pagination.current_page === pagination.total_pages }">
          <button
            class="page-link"
            @click="changePage(pagination.current_page + 1)"
            :disabled="pagination.current_page === pagination.total_pages"
          >
            Siguiente
            <i class="fas fa-chevron-right ms-1"></i>
          </button>
        </li>
      </ul>
      
      <div class="text-center text-muted">
        <i class="fas fa-info-circle me-1"></i>
        Mostrando {{ ((pagination.current_page - 1) * pagination.per_page) + 1 }} 
        a {{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }} 
        de {{ pagination.total }} empresas
      </div>
    </nav>
  </div>

    <!-- Modales -->
    <!-- Modal Ver/Editar Empresa -->
    <div class="modal fade" id="empresaModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-building me-2"></i>
              {{ isEditing ? 'Editar Empresa' : (isCreating ? 'Nueva Empresa' : 'Detalles de Empresa') }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveEmpresa" v-if="isEditing || isCreating">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label">RUC *</label>
                  <input
                    v-model="empresaForm.ruc"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.ruc }"
                    maxlength="13"
                    placeholder="Ingrese RUC de 13 dígitos"
                    @blur="validateRuc"
                  />
                  <div v-if="errors.ruc" class="invalid-feedback">{{ errors.ruc }}</div>
                </div>
                
                <div class="col-md-6">
                  <label class="form-label">Subdominio</label>
                  <input
                    v-model="empresaForm.subdomain"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.subdomain }"
                    placeholder="empresa-ejemplo"
                  />
                  <div v-if="errors.subdomain" class="invalid-feedback">{{ errors.subdomain }}</div>
                </div>
                
                <div class="col-12">
                  <label class="form-label">Razón Social *</label>
                  <input
                    v-model="empresaForm.razon_social"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.razon_social }"
                    @input="onRazonSocialInput"
                  />
                  <div v-if="errors.razon_social" class="invalid-feedback">{{ errors.razon_social }}</div>
                  <small class="text-muted mt-1 d-block">
                    Debug: Valor actual: "{{ empresaForm.razon_social }}" ({{ empresaForm.razon_social ? empresaForm.razon_social.length : 0 }} caracteres)
                  </small>
                  <button type="button" class="btn btn-sm btn-outline-info mt-1" @click="debugValidateRazonSocial">
                    <i class="fas fa-bug"></i> Debug Validación
                  </button>
                </div>
                
                <div class="col-md-6">
                  <label class="form-label">Nombre Comercial</label>
                  <input
                    v-model="empresaForm.trade_name"
                    type="text"
                    class="form-control"
                  />
                </div>
                
                <div class="col-md-6">
                  <label class="form-label">Tipo de Empresa</label>
                  <select v-model="empresaForm.company_type" class="form-select">
                    <option value="SA">Sociedad Anónima</option>
                    <option value="SRL">Sociedad de Responsabilidad Limitada</option>
                    <option value="EIRL">EIRL</option>
                    <option value="Natural">Persona Natural</option>
                    <option value="Other">Otro</option>
                  </select>
                </div>
                
                <div class="col-12">
                  <label class="form-label">Dirección *</label>
                  <textarea
                    v-model="empresaForm.direccion"
                    class="form-control"
                    :class="{ 'is-invalid': errors.direccion }"
                    rows="2"
                  ></textarea>
                  <div v-if="errors.direccion" class="invalid-feedback">{{ errors.direccion }}</div>
                </div>
                
                <div class="col-md-6">
                  <label class="form-label">Ciudad</label>
                  <input
                    v-model="empresaForm.ciudad"
                    type="text"
                    class="form-control"
                  />
                </div>
                
                <div class="col-md-6">
                  <label class="form-label">Email *</label>
                  <input
                    v-model="empresaForm.email"
                    type="email"
                    class="form-control"
                    :class="{ 'is-invalid': errors.email }"
                  />
                  <div v-if="errors.email" class="invalid-feedback">{{ errors.email }}</div>
                </div>
                
                <div class="col-md-6">
                  <label class="form-label">Teléfono *</label>
                  <input
                    v-model="empresaForm.telefono"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.telefono }"
                  />
                  <div v-if="errors.telefono" class="invalid-feedback">{{ errors.telefono }}</div>
                </div>
                
                <div class="col-md-6">
                  <label class="form-label">WhatsApp</label>
                  <input
                    v-model="empresaForm.whatsapp"
                    type="text"
                    class="form-control"
                  />
                </div>
                
                <div class="col-12">
                  <label class="form-label">Representante Legal *</label>
                  <input
                    v-model="empresaForm.legal_representative"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.legal_representative }"
                  />
                  <div v-if="errors.legal_representative" class="invalid-feedback">{{ errors.legal_representative }}</div>
                </div>
                
                <div class="col-md-6">
                  <label class="form-label">Email Rep. Legal *</label>
                  <input
                    v-model="empresaForm.legal_representative_email"
                    type="email"
                    class="form-control"
                    :class="{ 'is-invalid': errors.legal_representative_email }"
                  />
                  <div v-if="errors.legal_representative_email" class="invalid-feedback">{{ errors.legal_representative_email }}</div>
                </div>
                
                <div class="col-md-6">
                  <label class="form-label">Teléfono Rep. Legal *</label>
                  <input
                    v-model="empresaForm.legal_representative_phone"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.legal_representative_phone }"
                  />
                  <div v-if="errors.legal_representative_phone" class="invalid-feedback">{{ errors.legal_representative_phone }}</div>
                </div>
              </div>
            </form>
            
            <!-- Vista de solo lectura -->
            <div v-else class="row g-3">
              <div class="col-md-6">
                <strong>RUC:</strong> {{ selectedEmpresa?.ruc }}
              </div>
              <div class="col-md-6" v-if="selectedEmpresa?.subdomain">
                <strong>Subdominio:</strong> {{ selectedEmpresa?.subdomain }}
              </div>
              <div class="col-12">
                <strong>Razón Social:</strong> {{ selectedEmpresa?.razon_social }}
              </div>
              <div class="col-md-6">
                <strong>Nombre Comercial:</strong> {{ selectedEmpresa?.trade_name || '-' }}
              </div>
              <div class="col-md-6">
                <strong>Tipo:</strong> {{ getCompanyTypeText(selectedEmpresa?.company_type) }}
              </div>
              <div class="col-12">
                <strong>Dirección:</strong> {{ selectedEmpresa?.direccion }}
              </div>
              <div class="col-md-6">
                <strong>Ciudad:</strong> {{ selectedEmpresa?.ciudad || '-' }}
              </div>
              <div class="col-md-6">
                <strong>Email:</strong> {{ selectedEmpresa?.email }}
              </div>
              <div class="col-md-6">
                <strong>Teléfono:</strong> {{ selectedEmpresa?.telefono }}
              </div>
              <div class="col-md-6">
                <strong>WhatsApp:</strong> {{ selectedEmpresa?.whatsapp || '-' }}
              </div>
              <div class="col-12">
                <strong>Representante Legal:</strong> {{ selectedEmpresa?.legal_representative }}
              </div>
              <div class="col-md-6">
                <strong>Email Rep. Legal:</strong> {{ selectedEmpresa?.legal_representative_email }}
              </div>
              <div class="col-md-6">
                <strong>Teléfono Rep. Legal:</strong> {{ selectedEmpresa?.legal_representative_phone }}
              </div>
              <div class="col-md-6">
                <strong>Estado:</strong> 
                <span :class="`badge bg-${getStatusVariant(selectedEmpresa?.status)}`">
                  {{ getStatusText(selectedEmpresa?.status) }}
                </span>
              </div>
              <div class="col-md-6">
                <strong>Fecha Registro:</strong> {{ formatDate(selectedEmpresa?.created_at) }}
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              <i class="fas fa-times me-1"></i>
              {{ isEditing || isCreating ? 'Cancelar' : 'Cerrar' }}
            </button>
            <button
              v-if="!isEditing && !isCreating"
              type="button"
              class="btn btn-primary"
              @click="editEmpresa(selectedEmpresa)"
            >
              <i class="fas fa-edit me-1"></i>
              Editar
            </button>
            <button
              v-if="isEditing || isCreating"
              type="button"
              class="btn btn-primary"
              @click="saveEmpresa"
              :disabled="saving"
            >
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              <i v-if="!saving" :class="isCreating ? 'fas fa-plus me-1' : 'fas fa-save me-1'"></i>
              {{ isCreating ? 'Crear' : 'Guardar' }}
            </button>
          </div>
        </div>
      </div>
    </div>
</template>

<script>
import empresasService from '@/services/empresasService'
import { Modal } from 'bootstrap'
import themeMixin from '@/mixins/themeMixin'
import { notifySuccess, notifyError, notifyInfo } from '@/services/notificationService'
import { ConfirmationUtils } from '@/utils/confirmations'

export default {
  name: 'EmpresasList',
  mixins: [themeMixin],
  data() {
    return {
      empresas: [],
      stats: null,
      loading: false,
      saving: false,
      
      // Filtros
      filters: {
        search: '',
        status: '',
        company_type: ''
      },
      
      // Paginación
      pagination: {
        current_page: 1,
        per_page: 20,
        total: 0,
        total_pages: 0
      },
      
      // Modal estados
      isEditing: false,
      isCreating: false,
      selectedEmpresa: null,
      empresaModal: null,
      
      // Formulario
      empresaForm: {
        ruc: '',
        subdomain: '',
        razon_social: '',
        trade_name: '',
        company_type: 'SA',
        direccion: '',
        ciudad: '',
        telefono: '',
        whatsapp: '',
        email: '',
        legal_representative: '',
        legal_representative_email: '',
        legal_representative_phone: ''
      },
      
      // Validación
      errors: {},
      
      // Dropdown activo
      activeDropdown: null,
      // Estilo inline para el dropdown teleporteado
      dropdownStyle: {},
      
      // Debounce timer
      searchTimer: null
    }
  },
  
  computed: {
    
    // Estadísticas calculadas dinámicamente
    computedStats() {
      if (!this.empresas || this.empresas.length === 0) {
        return this.stats || {
          total_empresas: 0,
          empresas_activas: 0,
          empresas_inactivas: 0,
          empresas_suspendidas: 0
        }
      }
      
      return {
        total_empresas: this.empresas.length,
        empresas_activas: this.empresas.filter(emp => emp.status === 'active').length,
        empresas_inactivas: this.empresas.filter(emp => emp.status === 'inactive').length,
        empresas_suspendidas: this.empresas.filter(emp => emp.status === 'suspended').length
      }
    }
  },
  
  methods: {
    debouncedSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.pagination.current_page = 1
        this.loadEmpresas()
      }, 500)
    },
    async loadEmpresas() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.current_page,
          limit: this.pagination.per_page,
          ...this.filters
        }
        
        // Filtrar parámetros vacíos
        Object.keys(params).forEach(key => {
          if (!params[key]) delete params[key]
        })
        
        console.log('Cargando empresas con parámetros:', params)
        const response = await empresasService.getEmpresas(params)
        console.log('Respuesta del servicio:', response)
        
        if (response.success) {
          // Adaptar los datos del endpoint de subdominios al formato esperado
          this.empresas = (response.data || []).map(empresa => ({
            id: empresa.id,
            ruc: empresa.ruc,
            razon_social: empresa.razon_social,
            trade_name: empresa.trade_name,
            company_type: empresa.company_type,
            email: empresa.email || 'No especificado',
            telefono: empresa.telefono || 'No especificado',
            whatsapp: empresa.whatsapp || null,
            direccion: empresa.direccion || 'No especificado',
            ciudad: empresa.ciudad || 'No especificado',
            subdomain: empresa.subdomain,
            created_at: empresa.created_at,
            status: this.mapStatus(empresa.status), // Mapear status numérico a string
            legal_representative: empresa.legal_representative || 'No especificado',
            legal_representative_email: empresa.legal_representative_email || 'No especificado',
            legal_representative_phone: empresa.legal_representative_phone || 'No especificado'
          }))
          
          this.pagination = {
            ...this.pagination,
            ...response.pagination
          }
          
          console.log('Empresas procesadas:', this.empresas)
        }
      } catch (error) {
        console.error('Error al cargar empresas:', error)
        notifyError('Error al cargar empresas')
      } finally {
        this.loading = false
      }
    },
    
    async loadStats() {
      try {
        // Calcular estadísticas basándose en las empresas actuales
        if (this.empresas.length > 0) {
          const stats = {
            total_empresas: this.empresas.length,
            empresas_activas: this.empresas.filter(emp => emp.status === 'active').length,
            empresas_inactivas: this.empresas.filter(emp => emp.status === 'inactive').length,
            empresas_suspendidas: this.empresas.filter(emp => emp.status === 'suspended').length
          }
          this.stats = stats
          return
        }
        
        // Si no hay empresas cargadas, obtener desde el servicio
        const response = await empresasService.getStats()
        if (response.success) {
          this.stats = response.data
        }
      } catch (error) {
        console.error('Error al cargar estadísticas:', error)
        // Estadísticas por defecto
        this.stats = {
          total_empresas: 0,
          empresas_activas: 0,
          empresas_inactivas: 0,
          empresas_suspendidas: 0
        }
      }
    },
    
    changePage(page) {
      if (page >= 1 && page <= this.pagination.total_pages) {
        this.pagination.current_page = page
        this.loadEmpresas()
      }
    },
    
    getVisiblePages() {
      const current = this.pagination.current_page
      const total = this.pagination.total_pages
      const pages = []
      
      // Mostrar páginas alrededor de la actual
      const start = Math.max(1, current - 2)
      const end = Math.min(total, current + 2)
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      
      return pages
    },
    
    openCreateModal() {
      this.isCreating = true
      this.isEditing = false
      this.selectedEmpresa = null
      this.resetForm()
      this.showModal()
    },
    
    viewEmpresa(empresa) {
      this.isEditing = false
      this.isCreating = false
      this.selectedEmpresa = empresa
      this.showModal()
    },
    
    editEmpresa(empresa) {
      this.isEditing = true
      this.isCreating = false
      this.selectedEmpresa = empresa
      
      // Llenar formulario con datos de la empresa
      Object.keys(this.empresaForm).forEach(key => {
        this.empresaForm[key] = empresa[key] || ''
      })
      
      this.errors = {}
      this.showModal()
    },
    
    async saveEmpresa() {
      this.errors = {}
      
      // Asegurar que razon_social sea string
      if (this.empresaForm.razon_social === null || this.empresaForm.razon_social === undefined) {
        this.empresaForm.razon_social = ''
      }
      
      // Log de debug - verificar datos del formulario
      console.log('Datos del formulario antes de validar:', this.empresaForm)
      console.log('Campo razon_social:', this.empresaForm.razon_social, 'tipo:', typeof this.empresaForm.razon_social, 'length:', this.empresaForm.razon_social ? this.empresaForm.razon_social.length : 0)
      console.log('Campo razon_social trimmed:', this.empresaForm.razon_social ? this.empresaForm.razon_social.trim() : 'undefined/null')
      
      // Limpiar datos antes de enviar
      const cleanData = {}
      Object.keys(this.empresaForm).forEach(key => {
        if (typeof this.empresaForm[key] === 'string') {
          cleanData[key] = this.empresaForm[key].trim()
        } else {
          cleanData[key] = this.empresaForm[key]
        }
      })
      
      console.log('Datos limpios para enviar:', cleanData)
      console.log('Campo razon_social limpio:', cleanData.razon_social, 'tipo:', typeof cleanData.razon_social, 'length:', cleanData.razon_social ? cleanData.razon_social.length : 0)
      
      // Validar datos con datos limpios
      const validation = empresasService.validateEmpresaData(cleanData)
      if (!validation.isValid) {
        console.log('Errores de validación:', validation.errors)
        this.errors = validation.errors
        return
      }
      
      this.saving = true
      try {
        let response
        
        if (this.isCreating) {
          console.log('Enviando datos para crear empresa:', cleanData)
          response = await empresasService.createEmpresa(cleanData)
        } else {
          response = await empresasService.updateEmpresa(this.selectedEmpresa.id, this.empresaForm)
        }
        
        if (response.success) {
          this.toast.success(
            this.isCreating ? 'Empresa creada correctamente' : 'Empresa actualizada correctamente'
          )
          this.hideModal()
          this.loadEmpresas()
          this.loadStats()
        }
      } catch (error) {
        console.error('Error al guardar empresa:', error)
        notifyError('Error al guardar empresa')
      } finally {
        this.saving = false
      }
    },
    
    async changeStatus(empresaId, newStatus) {
      this.closeDropdown()
      
      // Buscar la empresa para obtener su nombre
      const empresa = this.empresas.find(emp => emp.id === empresaId)
      if (!empresa) return
      
      // Usar utilidad centralizada para confirmación
      const confirmed = await ConfirmationUtils.confirmStatusChange(empresa.razon_social)
      if (!confirmed) return
      
      try {
        const response = await empresasService.changeStatus(empresaId, newStatus)
        if (response.success) {
          // Actualizar el estado localmente de inmediato
          const empresaIndex = this.empresas.findIndex(emp => emp.id === empresaId)
          if (empresaIndex !== -1) {
            this.empresas[empresaIndex].status = newStatus
            this.$forceUpdate() // Forzar actualización de la vista
          }
          
          this.toast.success('Estado de empresa actualizado correctamente')
          // Las estadísticas se actualizan automáticamente via computed property
        }
      } catch (error) {
        console.error('Error al cambiar estado:', error)
        
        // Simular cambio de estado para demo (ya que no hay endpoint real)
        const empresaIndex = this.empresas.findIndex(emp => emp.id === empresaId)
        if (empresaIndex !== -1) {
          this.empresas[empresaIndex].status = newStatus
          this.$forceUpdate()
          notifySuccess('Estado actualizado correctamente (modo demo)')
        }
      }
    },
    
    async deleteEmpresa(empresa) {
      // Usar utilidad centralizada para confirmación de eliminación
      const confirmed = await ConfirmationUtils.confirmDelete(empresa.razon_social, 'empresa')
      if (!confirmed) return
      
      try {
        const response = await empresasService.deleteEmpresa(empresa.id)
        if (response.success) {
          notifySuccess('Empresa eliminada correctamente')
          this.loadEmpresas()
          this.loadStats()
        }
      } catch (error) {
        console.error('Error al eliminar empresa:', error)
        notifyError('Error al eliminar empresa')
      }
    },
    
    resetForm() {
      Object.keys(this.empresaForm).forEach(key => {
        this.empresaForm[key] = key === 'company_type' ? 'SA' : ''
      })
      this.errors = {}
    },
    
    showModal() {
      if (!this.empresaModal) {
        this.empresaModal = new Modal(document.getElementById('empresaModal'))
      }
      this.empresaModal.show()
    },
    
    hideModal() {
      if (this.empresaModal) {
        this.empresaModal.hide()
      }
    },
    
    // Manejo del dropdown manual (posicionado y teleporteado)
    toggleDropdown(event, empresa) {
      event.stopPropagation()

      // Si ya está abierto para esta empresa, cerrarlo
      if (this.activeDropdown === empresa.id) {
        this.activeDropdown = null
        this.dropdownStyle = {}
        return
      }

      // Calcular posición relativa al viewport y convertir a coordenadas de documento
      const btn = event.currentTarget
      const rect = btn.getBoundingClientRect()
      const top = rect.bottom + window.scrollY

      // Determinar si hay suficiente espacio a la derecha; si no, alinear a la derecha del botón (abrir hacia la izquierda)
      const spaceRight = window.innerWidth - rect.left
      const preferredMenuWidth = Math.max(220, rect.width + 40) // ancho aproximado del menú

      if (spaceRight < preferredMenuWidth) {
        // Abrir hacia la izquierda: alinear el borde derecho del menú con el borde derecho del botón
        const right = window.innerWidth - rect.right + window.scrollX
        this.dropdownStyle = {
          position: 'absolute',
          top: `${top}px`,
          right: `${right}px`,
          left: 'auto',
          minWidth: `${rect.width}px`,
          zIndex: 2050
        }
      } else {
        // Abrir hacia la derecha (por defecto)
        const left = rect.left + window.scrollX
        this.dropdownStyle = {
          position: 'absolute',
          top: `${top}px`,
          left: `${left}px`,
          right: 'auto',
          minWidth: `${rect.width}px`,
          zIndex: 2050
        }
      }

      this.activeDropdown = empresa.id
    },
    
    closeDropdown() {
      this.activeDropdown = null
      this.dropdownStyle = {}
    },
    
    // Nuevas funcionalidades del dropdown
    async generateAccessCredentials(empresa) {
      this.closeDropdown()
      try {
        const response = await empresasService.generateCredentials(empresa.id)
        if (response.success) {
          notifySuccess('Credenciales generadas correctamente')
          
          // Mostrar las credenciales en un modal informativo
          await ConfirmationUtils.showInfo({
            title: 'Credenciales de acceso generadas',
            html: `Las credenciales para <strong>"${empresa.razon_social}"</strong> han sido generadas:<br><br><div class="text-start"><strong>Usuario:</strong> <code class="bg-light p-1 rounded">${response.data.username}</code><br><strong>Contraseña:</strong> <code class="bg-light p-1 rounded">${response.data.password}</code></div><br><small class="text-warning">⚠️ Guarde estas credenciales en un lugar seguro. No se podrán recuperar después.</small>`,
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#10b981'
          })
        }
      } catch (error) {
        console.error('Error al generar credenciales:', error)
        notifyError('Error al generar credenciales')
      }
    },
    
    async resetPassword(empresa) {
      this.closeDropdown()
      
      // Usar utilidad centralizada para confirmación destructiva
      const confirmed = await ConfirmationUtils.confirmDestructiveAction('Resetear contraseña', empresa.razon_social)
      if (!confirmed) return
      
      try {
        const response = await empresasService.resetPassword(empresa.id)
        if (response.success) {
          notifySuccess('Contraseña reseteada correctamente')
          
          // Mostrar la nueva contraseña usando la utilidad
          await ConfirmationUtils.showInfo({
            title: 'Nueva contraseña generada',
            html: `La nueva contraseña para <strong>"${empresa.razon_social}"</strong> es:<br><br><code class="bg-light p-2 rounded">${response.data.new_password}</code><br><br><small class="text-muted">Guarda esta contraseña en un lugar seguro.</small>`
          })
        }
      } catch (error) {
        console.error('Error al resetear contraseña:', error)
        notifyError('Error al resetear contraseña')
      }
    },
    
    async exportData(empresa) {
      this.closeDropdown()
      try {
        const response = await empresasService.exportData(empresa.id)
        if (response.success) {
          // Crear enlace de descarga
          const blob = new Blob([response.data], { type: 'application/json' })
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `empresa_${empresa.ruc}_datos.json`
          a.click()
          window.URL.revokeObjectURL(url)
          notifySuccess('Datos exportados correctamente')
        }
      } catch (error) {
        console.error('Error al exportar datos:', error)
        notifyError('Error al exportar datos')
      }
    },
    
    duplicateEmpresa(empresa) {
      this.closeDropdown()
      // Llenar formulario con datos de la empresa para duplicar
      this.isCreating = true
      this.isEditing = false
      this.selectedEmpresa = null
      
      Object.keys(this.empresaForm).forEach(key => {
        if (key === 'ruc') {
          this.empresaForm[key] = '' // RUC debe ser único
        } else if (key === 'subdomain') {
          this.empresaForm[key] = '' // Subdominio debe ser único
        } else if (key === 'razon_social') {
          this.empresaForm[key] = `${empresa[key]} (Copia)`
        } else {
          this.empresaForm[key] = empresa[key] || ''
        }
      })
      
      this.errors = {}
      this.showModal()
      notifyInfo('Datos copiados. Modifique RUC y Razón Social antes de guardar.')
    },
    
    // Métodos de utilidad
    mapStatus(status) {
      // Convertir status numérico del backend a string esperado por el frontend
      const statusMap = {
        1: 'active',
        0: 'inactive',
        2: 'suspended'
      }
      return statusMap[status] || 'active' // Por defecto active si no se reconoce
    },
    
    getStatusText(status) {
      return empresasService.getStatusText(status)
    },
    
    getStatusVariant(status) {
      const variants = {
        'active': 'success',
        'inactive': 'secondary',
        'suspended': 'warning'
      }
      return variants[status] || 'secondary'
    },
    
    getCompanyTypeText(companyType) {
      return empresasService.getCompanyTypeText(companyType)
    },
    
    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    
    formatTime(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    // Validar RUC automáticamente
    async validateRuc() {
      const ruc = this.empresaForm.ruc?.trim()
      
      // Limpiar error anterior
      this.errors.ruc = null
      
      if (!ruc) {
        return
      }
      
      // Validar formato básico
      if (!/^\d{13}$/.test(ruc)) {
        this.errors.ruc = 'El RUC debe tener exactamente 13 dígitos'
        return
      }
      
      try {
        // Validar formato con algoritmo local
        const formatValidation = await empresasService.validateRuc(ruc)
        if (!formatValidation.valido) {
          this.errors.ruc = 'El RUC tiene un formato inválido'
          return
        }
        
        // Verificar si ya existe (solo en modo creación)
        if (this.isCreating) {
          const existsCheck = await empresasService.checkRucExists(ruc)
          if (existsCheck.exists) {
            this.errors.ruc = 'Este RUC ya está registrado en el sistema'
            return
          }
        }
        
        // Si llega aquí, el RUC es válido
        console.log('RUC válido:', ruc)
      } catch (error) {
        console.error('Error al validar RUC:', error)
        this.errors.ruc = 'Error al validar el RUC. Intente nuevamente.'
      }
    },
    
    // Debug para campo razon_social
    onRazonSocialInput() {
      console.log('Input razon_social:', {
        valor: this.empresaForm.razon_social,
        tipo: typeof this.empresaForm.razon_social,
        length: this.empresaForm.razon_social ? this.empresaForm.razon_social.length : 0,
        trimmed: this.empresaForm.razon_social ? this.empresaForm.razon_social.trim() : '',
        trimmed_length: this.empresaForm.razon_social ? this.empresaForm.razon_social.trim().length : 0
      })
      
      // Limpiar error si el campo tiene contenido
      if (this.empresaForm.razon_social && this.empresaForm.razon_social.trim().length > 0) {
        this.errors.razon_social = null
      }
    },
    
    // Método de debug para probar validación manual
    debugValidateRazonSocial() {
      console.log('=== DEBUG VALIDACIÓN RAZON_SOCIAL ===')
      console.log('Valor actual:', this.empresaForm.razon_social)
      console.log('Tipo:', typeof this.empresaForm.razon_social)
      console.log('Length:', this.empresaForm.razon_social ? this.empresaForm.razon_social.length : 0)
      console.log('Trimmed:', this.empresaForm.razon_social ? this.empresaForm.razon_social.trim() : '')
      console.log('Trimmed length:', this.empresaForm.razon_social ? this.empresaForm.razon_social.trim().length : 0)
      
      const simpleValidation = empresasService.validateRazonSocialOnly(this.empresaForm.razon_social)
      const fullValidation = empresasService.validateEmpresaData({ razon_social: this.empresaForm.razon_social })
      
      console.log('Validación simple:', simpleValidation)
      console.log('Validación completa:', fullValidation)
      console.log('=====================================')
      
      alert(`Debug Razón Social:\nValor: "${this.empresaForm.razon_social}"\nLength: ${this.empresaForm.razon_social ? this.empresaForm.razon_social.length : 0}\nTrimmed: "${this.empresaForm.razon_social ? this.empresaForm.razon_social.trim() : ''}"\n\nValidación Simple - ¿Válido?: ${simpleValidation.isValid}\nValidación Completa - ¿Válido?: ${fullValidation.isValid}\nErrores: ${JSON.stringify(fullValidation.errors)}`)
    },
    
    // Inicializar dropdowns de Bootstrap
    async initializeDropdowns() {
      try {
        const { Dropdown } = await import('bootstrap')
        // Encontrar todos los botones de dropdown y inicializarlos
        const dropdownButtons = document.querySelectorAll('[data-bs-toggle="dropdown"]')
        dropdownButtons.forEach(button => {
          if (!button._dropdown) {
            try {
              button._dropdown = new Dropdown(button)
            } catch (error) {
              console.warn('Error initializing dropdown:', error)
            }
          }
        })
      } catch (error) {
        console.warn('Bootstrap not available:', error)
      }
    }
  },
  
  mounted() {
    this.loadEmpresas()
    this.loadStats()
    
    // Agregar event listener para cerrar dropdown al hacer clic fuera
    document.addEventListener('click', this.closeDropdown)
    
    // Inicializar dropdowns de Bootstrap después de que el DOM esté listo
    this.$nextTick(() => {
      this.initializeDropdowns()
    })
  },
  
  updated() {
    // Reinicializar dropdowns después de cada actualización
    this.$nextTick(() => {
      this.initializeDropdowns()
    })
  },
  
  beforeUnmount() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer)
    }
    // Remover event listener
    document.removeEventListener('click', this.closeDropdown)
  }
}
</script>

<style>
.card {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 12px;
}

/* Container principal */
.empresas-container {
  max-width: 100%;
  margin: 0;
  padding: 1.5rem;
  background-color: var(--secondary-bg-main);
  min-height: 100vh;
  width: 100%;
  transition: background-color 0.3s ease;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  background: var(--card-bg);
  padding: 2rem;
  border-radius: 12px;
  box-shadow: var(--shadow-md);
  transition: background-color 0.3s ease;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  transition: color 0.3s ease;
}

.page-title i {
  color: #6366f1;
}

.page-subtitle {
  color: var(--text-secondary);
  margin: 0.25rem 0 0 0;
  font-size: 1rem;
  display: flex;
  align-items: center;
  transition: color 0.3s ease;
}

.page-subtitle i {
  color: #9ca3af;
}

.btn-primary-custom {
  background: #6366f1;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
}

.btn-primary-custom:hover {
  background: #818cf8;
  transform: translateY(-1px);
  color: white;
}

/* Estadísticas Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background: var(--card-bg);
  padding: 2rem 1.5rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: var(--shadow-md);
  border-left: 4px solid var(--border-main);
  transition: transform 0.2s, background-color 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-card.stat-total { border-left-color: #6366f1; }
.stat-card.stat-active { border-left-color: #10b981; }
.stat-card.stat-inactive { border-left-color: #4b5563; }
.stat-card.stat-suspended { border-left-color: #f59e0b; }

.stat-icon {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  opacity: 0.8;
}

.stat-card.stat-total .stat-icon { color: #6366f1; }
.stat-card.stat-active .stat-icon { color: #10b981; }
.stat-card.stat-inactive .stat-icon { color: #4b5563; }
.stat-card.stat-suspended .stat-icon { color: #f59e0b; }

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.stat-label {
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  transition: color 0.3s ease;
}

/* Filtros */
.filters-section {
  background: var(--card-bg);
  padding: 1.5rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  box-shadow: var(--shadow-md);
  display: flex;
  flex-wrap: nowrap;
  gap: 1rem;
  align-items: center;
  width: 100%;
  transition: background-color 0.3s ease;
  flex-direction: row;
}

.search-box {
  position: relative;
  min-width: 280px;
  flex: 1 1 auto;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  transition: color 0.3s ease;
}

.search-input {
  width: 100%;
  padding: 0.875rem 0.875rem 0.875rem 3rem;
  border: 1px solid var(--border-main);
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
  background-color: var(--secondary-bg-main);
  color: var(--text-primary);
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-main);
  box-shadow: 0 0 0 0.2rem rgba(14, 165, 233, 0.15);
}

.filter-group {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  align-items: center;
  flex: 0 0 auto;
  flex-wrap: nowrap;
}

@media (min-width: 769px) {
  .filters-section {
    flex-direction: row !important;
    flex-wrap: nowrap !important;
  }

  .filter-group {
    flex-direction: row !important;
    align-items: center !important;
  }
}

.filter-select {
  padding: 0.75rem 0.9rem;
  border: 1px solid var(--border-main);
  border-radius: 8px;
  background: var(--secondary-bg-main);
  color: var(--text-primary);
  min-width: 170px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
}

.filter-select:focus {
  outline: none;
  border-color: var(--primary-main);
  box-shadow: 0 0 0 0.2rem rgba(14, 165, 233, 0.15);
}

/* Lista de empresas */
.empresas-list {
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  overflow: visible;
  margin-bottom: 1.5rem;
  transition: background-color 0.3s ease;
}

.table-responsive {
  border-radius: 12px;
  padding: 0.5rem 1rem 1rem;
  background-color: var(--card-bg);
  border: 1px solid var(--border-main);
  transition: background-color 0.3s ease;
}

.table {
  margin-bottom: 0;
  font-size: 0.95rem;
}

.table th {
  font-weight: 600;
  background-color: var(--secondary-bg-soft) !important;
  color: var(--text-primary) !important;
  border-bottom: 2px solid var(--border-color);
  padding: 1.25rem 1rem;
  white-space: nowrap;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.table td {
  padding: 1.25rem 1rem;
  vertical-align: middle;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--card-bg);
  color: var(--text-primary);
  transition: background-color 0.3s ease, color 0.3s ease;
}

.loading-state, .empty-state {
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

.spinner {
  width: 2rem;
  height: 2rem;
  border: 3px solid var(--border-light);
  border-top: 3px solid var(--primary-main);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state i {
  font-size: 3rem;
  color: var(--text-muted);
  margin-bottom: 1rem;
}

.empty-state h3 i {
  font-size: 1.25rem;
  color: var(--accent-warning);
}

.empty-state p i {
  font-size: 0.875rem;
  color: var(--text-secondary);
}

/* Grid de empresas */
.empresas-grid {
  display: grid;
  gap: 1px;
  background: var(--border-main);
}

.empresa-card {
  background: var(--card-bg);
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.3s ease;
}

.empresa-card:hover {
  background: var(--secondary-bg-muted);
}

.empresa-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.empresa-info h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  transition: color 0.3s ease;
}

.empresa-ruc {
  color: #6366f1;
  font-weight: 500;
  margin: 0.25rem 0 0 0;
}

.empresa-trade {
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin: 0.25rem 0 0 0;
  transition: color 0.3s ease;
}

.empresa-details {
  display: flex;
  gap: 1.5rem;
  flex: 2;
  justify-content: center;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
  transition: color 0.3s ease;
}

.detail-item i {
  color: var(--text-muted);
  width: 1rem;
  transition: color 0.3s ease;
}

/* Status badges */
.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
}

.status-active { 
  background: var(--accent-success-light); 
  color: var(--accent-success); 
}
.status-inactive { 
  background: var(--secondary-bg-muted); 
  color: var(--text-secondary); 
}
.status-suspended { 
  background: var(--accent-warning-light); 
  color: var(--accent-warning); 
}

/* Acciones */
.empresa-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.action-btn {
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;
  position: relative;
  outline: none;
}

.action-btn:focus {
  outline: 2px solid rgba(99, 102, 241, 0.2);
  outline-offset: 2px;
}

.view-btn { 
  background: #f8fafc; 
  color: #64748b; 
  border-color: #e2e8f0;
}
.view-btn:hover { 
  background: #f1f5f9; 
  color: #475569;
  border-color: #cbd5e1;
}

.edit-btn { 
  background: #eff6ff; 
  color: #2563eb; 
  border-color: #bfdbfe;
}
.edit-btn:hover { 
  background: #dbeafe; 
  color: #1d4ed8;
  border-color: #93c5fd;
}

.more-btn { 
  background: #f8fafc; 
  color: #64748b; 
  border-color: #e2e8f0;
}
.more-btn:hover { 
  background: #f1f5f9; 
  color: #475569;
  border-color: #cbd5e1;
}

/* Iconos de acción con texto/emojis */
.action-icon {
  font-size: 1rem;
  line-height: 1;
  display: inline-block;
  font-style: normal;
}

/* Asegurar que los iconos FontAwesome se vean */
.action-btn i {
  font-family: "Font Awesome 5 Free", "Font Awesome 5 Pro", FontAwesome !important;
  font-weight: 900 !important;
  font-style: normal !important;
  font-variant: normal !important;
  text-transform: none !important;
  line-height: 1 !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  display: inline-block !important;
  font-size: 0.875rem !important;
}

/* Fallback para cuando FontAwesome no carga */
.action-btn i.fa-eye:before { content: "👁"; }
.action-btn i.fa-edit:before { content: "✏️"; }
.action-btn i.fa-ellipsis-v:before { content: "⋮"; }

/* Alternativa con caracteres más simples */
.action-btn.view-btn:empty:before { content: "👁"; }
.action-btn.edit-btn:empty:before { content: "✏"; }
.action-btn.more-btn:empty:before { content: "⋮"; }

/* Dropdown menu styling */
.dropdown {
  position: relative;
}

.dropdown-menu {
  border: none;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 0.5rem 0;
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1000;
  display: none;
  min-width: 220px;
  background-color: var(--card-bg);
  border: 1px solid var(--border-main);
}

.dropdown-menu.show {
  display: block;
}

.dropdown-header {
  display: block;
  padding: 0.5rem 1rem;
  margin-bottom: 0;
  font-size: 0.8rem;
  color: var(--text-muted);
  white-space: nowrap;
  font-weight: 600;
  text-transform: uppercase;
  background-color: var(--bg-tertiary);
  transition: background-color 0.3s ease, color 0.3s ease;
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 0.5rem 1rem;
  clear: both;
  font-weight: 400;
  color: var(--text-primary);
  text-align: inherit;
  text-decoration: none;
  white-space: nowrap;
  background-color: transparent;
  border: 0;
  transition: background-color 0.2s, color 0.3s ease;
  cursor: pointer;
}

.dropdown-item:hover,
.dropdown-item:focus {
  background-color: var(--sidebar-hover);
  color: var(--text-primary);
}

.dropdown-item.text-danger:hover {
  background-color: #fef2f2;
  color: #dc2626;
}

.dropdown-divider {
  height: 0;
  margin: 0.5rem 0;
  overflow: hidden;
  border-top: 1px solid var(--border-color);
}

.dropdown-item i {
  width: 1rem;
  text-align: center;
  margin-right: 0.5rem;
}

.badge {
  font-size: 0.75em;
}

.btn-group-sm .btn {
  font-size: 0.8rem;
}

/* Estilos para botones de acciones */
.action-buttons .btn {
  padding: 0;
  font-size: 0.9rem;
  min-width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-width: 1px;
  margin: 0;
}

.action-buttons {
  display: inline-flex;
  gap: 0.35rem;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
}

.action-dropdown .dropdown-toggle {
  width: 36px;
  height: 36px;
  padding: 0;
}

.action-buttons .btn i {
  font-size: 1rem;
  line-height: 1;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Mostrar emojis como respaldo si Font Awesome no carga */
.action-buttons .btn i::before {
  font-family: "Font Awesome 6 Free", "Font Awesome 5 Free";
  font-weight: 900;
  font-style: normal;
}

/* Si Font Awesome no está disponible, mostrar emoji */
.action-buttons .btn i:not([class*="fa-"])::before {
  content: "";
}

.action-buttons .btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Asegurar que los iconos Font Awesome se muestren */
.fas, .fa {
  font-family: "Font Awesome 6 Free", "Font Awesome 5 Free" !important;
  font-weight: 900 !important;
  font-style: normal !important;
}

.dropdown-menu {
  font-size: 0.9rem;
}

.pagination {
  margin-bottom: 0;
}

.modal-lg {
  max-width: 800px;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #dc3545;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.text-muted {
  color: #6c757d !important;
}

/* Responsive improvements */
@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.85rem;
  }
  
  .btn-group-sm .btn {
    padding: 0.25rem 0.5rem;
  }
  
  .modal-lg {
    max-width: 95%;
  }
}

/* Custom utility classes */
.cursor-pointer {
  cursor: pointer;
}

.border-primary {
  border-color: #0d6efd !important;
}

.border-success {
  border-color: #198754 !important;
}

.border-warning {
  border-color: #ffc107 !important;
}

.border-secondary {
  border-color: #6c757d !important;
}

/* Mejoras de legibilidad */
.table-hover tbody tr:hover {
  background-color: var(--sidebar-hover) !important;
}

.card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.table th {
  font-weight: 600;
  border-bottom: 2px solid var(--border-color);
}

.badge {
  font-size: 0.75em;
  padding: 0.35em 0.65em;
}

/* Mejor contraste para texto */
.text-dark {
  color: var(--text-primary) !important;
}

.bg-light {
  background-color: var(--bg-tertiary) !important;
}

/* Responsive */
@media (max-width: 768px) {
  .empresas-container { 
    padding: 1rem; 
  }
  
  .page-header { 
    flex-direction: column; 
    gap: 1rem; 
    text-align: center; 
  }
  
  .filters-section { 
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-group { 
    width: 100%;
    justify-content: flex-start;
  }
  
  .filter-select {
    width: 100%;
  }
  
  .search-box {
    min-width: auto;
  }
  
  .empresa-card { 
    flex-direction: column; 
    gap: 1rem; 
    text-align: center; 
  }
  
  .empresa-details { 
    justify-content: center; 
    flex-wrap: wrap; 
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
}
</style>

